import React from 'react';
import { important, px } from 'csx';
import { classes, style } from 'typestyle';
import { centerCenter } from 'csstips';
import { GoogleIcon } from '../project-happy/atoms/icons/GoogleIcon';
import { colorGunmetal, colorWhite, rebrand } from '../../theme/color';
import { spacing } from '../../theme/spacing';
import { transitionQuickEase } from '../../theme/transition';

const styles = {
  wrapper: style(centerCenter, {
    display: 'inline-flex',
    padding: `${spacing.SMALL} ${spacing.DEFAULT}`,
    backgroundColor: colorWhite.toString(),
    color: colorGunmetal.toString(),
    font: 'inherit',
    border: `1px solid ${rebrand.neutralOnDark[30].toString()}`,
    borderRadius: px(32),
    transition: `border-color ${transitionQuickEase}`,
    textDecoration: important('none'), // Google buttons should NEVER have underlining
    $nest: {
      '&:hover, &:focus': {
        borderColor: rebrand.neutralOnDark[50].toString(),
      },
    },
  }),
  text: style({
    marginLeft: spacing.SMALL,
    fontWeight: 500,
    fontFamily:
      'roboto,-apple-system,BlinkMacSystemFont,segoe ui,oxygen,ubuntu,cantarell,fira sans,droid sans,helvetica neue,sans-serif',
  }),
  disabled: style({
    color: rebrand.neutralOnDark[30].toString(),
    cursor: 'default',
    $nest: {
      '&:hover, &:focus': {
        borderColor: rebrand.neutralOnDark[30].toString(),
      },
    },
  }),
};

interface GoogleButtonProps {
  className?: string;
  href?: string;
  text?: string;
  onClick?: (event: React.MouseEvent<any>) => void;
  disabled?: boolean;
}

export const GoogleButton: React.FC<GoogleButtonProps> = ({
  className,
  href,
  text = 'Continue with Google',
  onClick,
  disabled = false,
  ...rest
}) => {
  const wording = <span className={styles.text}>{text}</span>;

  const wrapperClasses = classes(styles.wrapper, className);

  if (href)
    return (
      <a className={wrapperClasses} href={href} onClick={onClick} {...rest}>
        <GoogleIcon />
        {wording}
      </a>
    );

  return (
    <button
      className={classes(wrapperClasses, disabled && styles.disabled)}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      <GoogleIcon color={disabled ? rebrand.neutralOnDark[30].toString() : undefined} />
      {wording}
    </button>
  );
};
